'use client';

import React from 'react';

import { Card, Heading } from '@ui';
import { Container, Section } from '@layout';
import { withOptionalId } from '@lib/withOptionalId';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface EmptyStatesDemoProps {
  sectionID?: string;
  classes?: string;
  isSpanish?: boolean;
}

const EmptyStatesDemo: React.FC<EmptyStatesDemoProps> = ({
  isSpanish = false,
  sectionID = 'empty-states',
  classes = '',
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container>
        <Heading as='h3'>{t('Empty, error & success', 'Vacios, error y confirmacion')}</Heading>
        <p className='sub full'>
          {t(
            'Reserve these surfaces for recoverable context—pair a short explanation with a clear next step, including positive confirmation after success.',
            'Reserva estas superficies para contexto recuperable—explica en breve y ofrece un siguiente paso claro, incluida la confirmacion positiva tras el exito.'
          )}
        </p>
        <div className='empty-states-demo__grid'>
          <Card className='surface-1 empty-states-demo__panel'>
            <Heading as='h4'>{t('Empty list', 'Lista vacia')}</Heading>
            <p className='small empty-states-demo__lead empty-states-demo__lead--neutral'>
              {t('No projects match these filters.', 'Ningun proyecto coincide con estos filtros.')}
            </p>
            <p className='small empty-states-demo__hint'>
              {t(
                'Clear filters or create a project to get started.',
                'Quita filtros o crea un proyecto para empezar.'
              )}
            </p>
          </Card>
          <Card className='surface-1 empty-states-demo__panel empty-states-demo__panel--error'>
            <Heading as='h4'>{t('Error', 'Error')}</Heading>
            <p className='small empty-states-demo__lead empty-states-demo__lead--error'>
              {t('Could not load results.', 'No se pudieron cargar los resultados.')}
            </p>
            <p className='small empty-states-demo__hint'>
              {t(
                'Check your connection and try again.',
                'Comprueba la conexion e intentalo de nuevo.'
              )}
            </p>
          </Card>
          <Card className='surface-1 empty-states-demo__panel empty-states-demo__panel--success'>
            <Heading as='h4'>{t('Success', 'Exito')}</Heading>
            <p className='small empty-states-demo__lead empty-states-demo__lead--success'>
              {t('Changes saved.', 'Cambios guardados.')}
            </p>
            <p className='small empty-states-demo__hint'>
              {t('Your team can see the update.', 'Tu equipo ya puede ver la actualizacion.')}
            </p>
          </Card>
        </div>
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<article class="card surface-1">
  <h4>Empty</h4>
  <p class="small">Nothing here yet.</p>
</article>`}
        />
      </Container>
    </Section>
  );
};

export default EmptyStatesDemo;
